import React from "react";
import { useQuote } from "../context/QuoteContext";
import { parseDurationDiscounts, freeRitiroPeriods } from "../utils";
import { Switch } from "./ui/switch";
import { Gauge } from "lucide-react";

const fmt = (n) => Number(n).toLocaleString("it-IT", { maximumFractionDigits: 1 });

const MiniGauge = ({ months, value, redMax, yellowMax, max }) => {
  const clamp = (n) => Math.max(0, Math.min(100, n));
  const redW = clamp((redMax / max) * 100);
  const yellowW = clamp(((yellowMax - redMax) / max) * 100);
  const greenW = clamp(((max - yellowMax) / max) * 100);
  const markerLeft = clamp((value / max) * 100);
  const zone = value < redMax ? "Poco redditizio" : value < yellowMax ? "Nella media" : "Molto redditizio";
  const zoneColor = value < redMax ? "text-red-600" : value < yellowMax ? "text-amber-600" : "text-emerald-600";

  return (
    <div data-testid={`gauge-${months}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-semibold text-slate-600">{months} {months === 1 ? "mese" : "mesi"}</span>
        <span className={`text-sm font-display font-bold ${zoneColor}`} data-testid={`gauge-value-${months}`}>
          €{value.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}<span className="text-[10px] font-normal text-slate-400">/m³·mese</span>
        </span>
      </div>
      <div className="relative h-5 rounded-full overflow-hidden flex">
        <div className="h-full bg-red-400" style={{ width: `${redW}%` }} />
        <div className="h-full bg-amber-300" style={{ width: `${yellowW}%` }} />
        <div className="h-full bg-emerald-400" style={{ width: `${greenW}%` }} />
        <div data-testid={`gauge-thresholds-${months}`} title="Soglie rosso / giallo / fondo scala">
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-[9px] font-bold text-white tabular-nums pointer-events-none [text-shadow:0_1px_1.5px_rgba(0,0,0,0.55)]" style={{ left: `${redW}%` }}>{fmt(redMax)}</span>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 text-[9px] font-bold text-white tabular-nums pointer-events-none [text-shadow:0_1px_1.5px_rgba(0,0,0,0.55)]" style={{ left: `${redW + yellowW}%` }}>{fmt(yellowMax)}</span>
          <span className="absolute top-1/2 -translate-y-1/2 text-[9px] font-bold text-white tabular-nums pointer-events-none [text-shadow:0_1px_1.5px_rgba(0,0,0,0.55)]" style={{ right: 3 }}>{fmt(max)}</span>
        </div>
        <div className="absolute top-0 bottom-0 w-[2px] bg-slate-900 -translate-x-1/2 transition-all duration-500"
          style={{ left: `${markerLeft}%` }} />
      </div>
      <div className="flex items-center mt-0.5">
        <span className={`text-[10px] ${zoneColor}`}>{zone}</span>
      </div>
    </div>
  );
};

export const PriceGauge = ({ volume: volumeProp, monthly: monthlyProp }) => {
  const {
    settings, profitMonthly, profitVolume, ritiro, ritiroTotal, ritiroAutoTotal,
    smontaggioTotal, smontaggioAutoTotal, imballaggioTotal, imballaggioAutoTotal,
    gaugeToggles, setGaugeToggles, packingMargin, effectiveGaugePeriods,
  } = useQuote();

  const volume = volumeProp != null ? volumeProp : profitVolume;
  const monthly = monthlyProp != null ? monthlyProp : profitMonthly;

  const periods = effectiveGaugePeriods;
  const durDiscs = parseDurationDiscounts(settings?.durationDiscounts);
  const pct = (n) => { const d = durDiscs.find((x) => Number(x.months) === n); return d ? Number(d.percent) : 0; };

  const t = gaugeToggles || { transport: true, smontaggio: true, imballaggio: true };
  const toggle = (k) => setGaugeToggles((g) => ({ ...g, [k]: !g[k] }));
  const freeSet = freeRitiroPeriods(ritiro);

  const compute = (N) => {
    if (!volume || volume <= 0) return 0;
    const discounted = monthly * (1 - pct(N) / 100);
    const tDelta = t.transport
      ? (freeSet.includes(N) ? -ritiroAutoTotal : (ritiroTotal - ritiroAutoTotal))
      : 0;
    const sDelta = t.smontaggio ? (smontaggioTotal - smontaggioAutoTotal) : 0;
    const iDelta = t.imballaggio ? (imballaggioTotal - imballaggioAutoTotal) : 0;
    const pDelta = t.packing !== false ? packingMargin : 0;
    return (discounted + (tDelta + sDelta + iDelta + pDelta) / N) / volume;
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="price-gauge">
      <div className="flex items-center gap-3 mb-1">
        <Gauge className="h-5 w-5 text-[#1a5f5a]" />
        <h3 className="font-display font-semibold text-lg">Profit-o-metro 📊</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">Prezzo medio €/m³ al mese per durata. Verde = più redditizio per noi, rosso = meno.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {periods.map((p) => (
          <MiniGauge key={p.months} months={p.months} value={compute(p.months)} redMax={p.redMax} yellowMax={p.yellowMax} max={p.max} />
        ))}
      </div>

      <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-slate-100">
        {[["transport", "Considera trasporto"], ["smontaggio", "Considera smontaggio"], ["imballaggio", "Considera imballaggio"], ["packing", "Considera materiali imballaggio"]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <Switch checked={t[k] !== false} onCheckedChange={() => toggle(k)} data-testid={`gauge-toggle-${k}`} />
            {label}
          </label>
        ))}
      </div>
    </div>
  );
};
